// Workspace column sizing + stage zoom math for the feedback markup editor.
// Pure functions so clamping and anchoring are unit-testable.

export const TRAY_DEFAULT_PX = 176;
export const TRAY_MIN_PX = 140;
export const TRAY_MAX_PX = 420;
export const PANEL_DEFAULT_PX = 344;
export const PANEL_MIN_PX = 280;
export const PANEL_MAX_PX = 640;
// The stage column must stay usable no matter how far the rails grow.
export const STAGE_MIN_PX = 560;
// Each 8px splitter overlaps its neighboring rails by 3px on both sides, so
// the two rendered handles consume 4px of real flex width in total.
export const WORKSPACE_SPLITTER_TOTAL_PX = 4;
// A three-column workspace is only viable when both rails, both splitters,
// and the minimum usable stage fit inside the actual workspace container.
export const RESIZABLE_WORKSPACE_MIN_PX =
  TRAY_MIN_PX + PANEL_MIN_PX + STAGE_MIN_PX + WORKSPACE_SPLITTER_TOTAL_PX;
// Below this viewport width the responsive breakpoints own the layout and
// stored widths are ignored (the ≤1020px slim modes and ≤800px stacking).
export const RESIZE_MIN_VIEWPORT_PX = 1021;

const LAYOUT_STORAGE_PREFIX = 'dha-feedback-workspace-layout';

export function clampWorkspaceWidths({ tray, panel, available }) {
  const budget = Math.max(
    0,
    (available || 0) - STAGE_MIN_PX - WORKSPACE_SPLITTER_TOTAL_PX,
  );
  const clampedTray = Math.round(
    Math.min(Math.max(tray, TRAY_MIN_PX), Math.min(TRAY_MAX_PX, Math.max(TRAY_MIN_PX, budget - PANEL_MIN_PX))),
  );
  const clampedPanel = Math.round(
    Math.min(
      Math.max(panel, PANEL_MIN_PX),
      Math.min(PANEL_MAX_PX, Math.max(PANEL_MIN_PX, budget - clampedTray)),
    ),
  );
  return { tray: clampedTray, panel: clampedPanel };
}

function layoutStorageKey(actorUserId) {
  return `${LAYOUT_STORAGE_PREFIX}:${actorUserId || 'anonymous'}`;
}

export function readStoredWorkspaceLayout(actorUserId) {
  try {
    const raw = window.localStorage.getItem(layoutStorageKey(actorUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.tray) || !Number.isFinite(parsed?.panel)) return null;
    return { tray: parsed.tray, panel: parsed.panel };
  } catch {
    return null;
  }
}

export function writeStoredWorkspaceLayout(actorUserId, layout) {
  try {
    window.localStorage.setItem(
      layoutStorageKey(actorUserId),
      JSON.stringify({ tray: layout.tray, panel: layout.panel }),
    );
  } catch {
    /* storage full/blocked — resizing still works for the session */
  }
}

export function clearStoredWorkspaceLayout(actorUserId) {
  try {
    window.localStorage.removeItem(layoutStorageKey(actorUserId));
  } catch {
    /* ignore */
  }
}

// ---- stage zoom ----

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;
export const ZOOM_BUTTON_FACTOR = 1.25;
export const ZOOM_WHEEL_FACTOR = 1.12;

export function clampZoom(zoom) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}

export function steppedZoom(current, direction) {
  return clampZoom(direction > 0 ? current * ZOOM_BUTTON_FACTOR : current / ZOOM_BUTTON_FACTOR);
}

export function wheelZoom(current, deltaY) {
  return clampZoom(deltaY < 0 ? current * ZOOM_WHEEL_FACTOR : current / ZOOM_WHEEL_FACTOR);
}

// Keeps the content point under the cursor stationary across a zoom change.
// cursorX/Y are offsets inside the scroll viewport's client box.
export function anchoredScrollPosition({ scrollLeft, scrollTop, cursorX, cursorY, prevZoom, nextZoom }) {
  const scale = nextZoom / prevZoom;
  return {
    scrollLeft: Math.max(0, (scrollLeft + cursorX) * scale - cursorX),
    scrollTop: Math.max(0, (scrollTop + cursorY) * scale - cursorY),
  };
}
